import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, NgModule, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgxSelectModule } from 'ngx-select-ex';
import { BehaviorSubject, Observable, Subscriber, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { Uuid } from '../../../../../graphql';
import { BxCalculatorConfig } from '../../../../core/components/bx-calculator';
import { BxCalculatorService } from '../../../../core/components/bx-calculator/bx-calculator.service';
import { CatalogueItemLinkTileModule } from '../../../../core/components/catalogue-item-link-tile/catalogue-item-link-tile.component';
import { TaxRateSelectorModule } from '../../../../core/components/tax-rate-selector/tax-rate-selector.component';
import { AutoFocusDirective } from '../../../../core/directives/auto-focus.directive';
import { CurrencyInputModule } from '../../../../core/directives/currency-input.directive';
import { SingleClickModule } from '../../../../core/directives/single-click.directive';
import { EventRecordTypes } from '../../../../core/models/event-record-types.model';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../core/services/bx-unverified-alert.service';
import { CataloguesService } from '../../../../core/services/catalogues.service';
import { EventRecordsService } from '../../../../core/services/event-records.service';
import { PermissionService } from '../../../../core/services/permission/permission.service';
import { PriceListService } from '../../../../core/services/price-list.service';
import { TaxService } from '../../../../data/logic/tax.service';
import { Estimate, JobPayment, JobVariation, JobVariationTask } from '../../../../data/models';
import { RepoCommonService } from '../../../../data/repositories/repo-common.service';
import { RepoEstimatesService } from '../../../../data/repositories/repo-estimates.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { SharedComponentsModule } from '../../../../shared-components.module';
import { moveInArray } from '../../../../utils/helper-functions/array.functions';
import { focusElement } from '../../../../utils/helper-functions/element.functions';
import { LocalizePath } from '../../../../utils/types/localize-path';
import { UiEstimateItemPillModule } from '../../../estimates/estimate-view/costings/ui-item-code-pill/ui-item-code-pill.component';
import { EstimateMeasurementCategoryVM } from '../../../estimates/estimate-view/plans/measurement/feat-estimate-measurements-v2/domain';
import { EstimateMeasurementCalculatorService } from '../../../estimates/estimate-view/plans/measurement/feat-estimate-measurements-v2/estimate-measurement-calculator.service';
import { EstimateMeasurementCategoryService } from '../../../estimates/estimate-view/plans/measurement/feat-estimate-measurements-v2/estimate-measurement-category.service';
import { TakeOffMeasurement, TakeOffService } from '../../../takeoff';

type VariationOption = JobVariationDetailComponent['variationTypes'][number];
type ScheduleChanges = { added: Set<Uuid>; removed: Set<Uuid> };

@UntilDestroy()
@Component({
    selector: 'app-job-variation-detail',
    templateUrl: './job-variation-detail.component.html',
    styleUrls: ['./job-variation-detail.component.scss'],
    standalone: false,
})
export class JobVariationDetailComponent implements OnInit {
    variation!: JobVariation;
    statuses: { name: JobVariation['Status']; disabled: null | boolean }[] = [];
    // @ts-ignore TS2564
    locale: string;
    editing = false;
    // @ts-ignore TS2564
    taxCode: string;
    parentTasks: any[] = [];
    translations = {
        variationTitleNumber: '',
        variation: '',
    };
    // @ts-ignore TS2564
    estimate: Estimate;
    isTakeOffAvailable = false;
    action = new EventEmitter<{ cancelled: boolean; variation: JobVariation; scheduleChanges?: ScheduleChanges }>();
    permissions = {
        canEditStatus: false,
        canRejectVariation: false,
        canAddItem: false,
        canModifyVariation: false,
        canSendVariation: false,
    };
    // cost items
    // @ts-ignore TS2564
    costItemSearch: string;
    // @ts-ignore TS2564
    costItemSuggestions$: Observable<any>;
    unitMeasures = [];
    isVariationInvoiced = false;
    // @ts-ignore TS2322
    invoices: JobPayment[] = null;
    variationTypes: { label: string; value: boolean }[] = [];
    isAdjustment$ = new BehaviorSubject(false);
    showAdjustment = false;
    labelMap?: { variation: string; adjustment: string };
    label$ = this.isAdjustment$.pipe(
        switchMap((isAdjustment) => (isAdjustment ? this.getTranslated('general.adjustment') : this.getTranslated('general.variation')))
    );
    scheduleChanges: ScheduleChanges = { added: new Set(), removed: new Set() };

    constructor(
        private bsModalRef: BsModalRef,
        public permissionService: PermissionService,
        private translateService: TranslateService,
        public taxService: TaxService,
        private bxCalculatorService: BxCalculatorService,
        private takeOffService: TakeOffService,
        private logger: BxLogger,
        private bxUnverifiedAlertService: BxUnverifiedAlertService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private priceListService: PriceListService,
        private repoEstimates: RepoEstimatesService,
        private cataloguesService: CataloguesService,
        private repoCommon: RepoCommonService,
        readonly eventRecordsService: EventRecordsService,
        private readonly estimateMeasurementCategoryService: EstimateMeasurementCategoryService,
        private readonly estimateMeasurementCalculatorService: EstimateMeasurementCalculatorService
    ) {
        this.translateService.get('general.dateFormat').subscribe((res) => {
            this.locale = res;
        });
        eventRecordsService.recordEvent(EventRecordTypes.VariationOpened);
    }

    ngOnInit(): void {
        this.setupIsAdjustment();
        combineLatest([this.getTranslated('general.adjustment'), this.getTranslated('general.variation')]).subscribe(
            ([adjustment, variation]) => {
                this.labelMap = { adjustment, variation };
                this.variationTypes = [
                    { label: variation, value: false },
                    { label: adjustment, value: true },
                ];
            }
        );
        this.editing = this.variation.Status === 'Pending';

        this.taxCode =
            this.variation.Job.CalculationMethod === 0 ? this.sharedData.UserData.tenant.TaxCode : this.variation.TaxRatesShortCode;
        this.parentTasks = this.getParentTasks();

        this.repoCommon.GetUnitMeasures().then((results) => {
            // @ts-ignore TS2322
            this.unitMeasures = results;
        });

        this.repoCommon.GetUnitMeasures().then((results) => {
            // @ts-ignore TS2322
            this.unitMeasures = results;
        });

        combineLatest([
            this.permissionService.getState('jobs.maintain-variations.edit-status'),
            this.permissionService.getState('jobs.maintain-variations.reject-variation'),
            this.permissionService.getState('jobs.maintain-variations.modify-variation'),
            this.permissionService.getState('jobs.maintain-variations.send-variation'),
            this.isAdjustment$,
        ]).subscribe((val) => {
            this.permissions.canEditStatus = val[0];
            this.permissions.canRejectVariation = val[1];
            this.permissions.canModifyVariation = val[2];
            this.permissions.canSendVariation = val[3];
            const isAdjustment = val[4];
            this.statuses = [
                { name: 'Pending', disabled: null },
                { name: 'Sent', disabled: null },
                { name: 'Accepted', disabled: null },
                { name: 'Rejected', disabled: !this.permissions.canRejectVariation },
                { name: 'Digitally Accepted', disabled: isAdjustment || null },
            ];
        });

        this.translations.variationTitleNumber = '??';
        this.label$.subscribe((text) => {
            this.translations.variation = text;
            this.translations.variationTitleNumber = `${text} #`;
        });
        if (this.variation.Job.ID_Estimate) {
            this.repoEstimates.GetEstimate(this.variation.Job.ID_Estimate).then((data) => {
                this.estimate = data.results[0] as any;
                if (this.estimate) {
                    this.repoEstimates.GetEstimatePlans(this.variation.Job.ID_Estimate).then(() => {
                        this.isTakeOffAvailable = this.estimate.Plans.length > 0;
                    });
                }
            });
        }

        if (this.variation.JobVariationTasks && !this.variation.JobVariationTasks.find((task) => task.Order !== 0)) {
            this.orderVariationItems();
        }

        // @ts-ignore TS2532
        this.variation.JobVariationTasks.sort((a, b) => {
            return a.Order - b.Order;
        });
        this.variation.calculateTotal();
        this.setIsVariationInvoiced();
    }
    variationTypeChange(selectedOption: VariationOption) {
        this.isAdjustment$.next(selectedOption.value);
    }
    private setupIsAdjustment() {
        this.showAdjustment =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';
        if (!this.showAdjustment) return;
        this.isAdjustment$.next(this.variation.IsAdjustment);
        this.isAdjustment$.subscribe((isAdjustment) => {
            this.variation.IsAdjustment = isAdjustment;
            const newVariation = `New ${this.labelMap?.variation}`;
            const newAdjustment = `New ${this.labelMap?.adjustment}`;

            if (isAdjustment) {
                this.variation.StartDate = undefined;
                this.variation.TimeDelay = 0;
            }
            if ([newVariation, newAdjustment].includes(this.variation.Description))
                this.variation.Description = isAdjustment ? newAdjustment : newVariation;

            if (this.variation.Status === 'Digitally Accepted' && isAdjustment) this.variation.Status = 'Pending';
        });
    }
    getTranslated(key: LocalizePath) {
        return this.translateService.get(key).pipe(
            take(1),
            map((val) => (typeof val === 'string' ? val : ''))
        );
    }

    getParentTasks() {
        // @ts-ignore TS2532
        const filtered = this.variation.Job.JobItems.filter((i) => {
            return !i.IsDeleted && i.IsParent && i.Description;
        });
        const ordered = _.orderBy(filtered, ['OrderNumber']);
        const map = ordered.map((ordered) => ({ ID_ParentTask: ordered.ID_JobItem, Description: ordered.Description }));
        return map;
    }

    setIsVariationInvoiced() {
        this.invoices = [];
        if (this.variation.Job === null) {
            this.isVariationInvoiced = false;
            return;
        }
        if (this.variation.ID_JobPayment) {
            this.isVariationInvoiced = true;
            // @ts-ignore TS2345
            this.invoices.push(this.variation.JobPayment);
            return;
        }
        // @ts-ignore TS2532
        this.variation.Job.JobPayments.forEach((jobInvoice) => {
            if (!jobInvoice.IsDeleted) {
                jobInvoice.JobPaymentItems.forEach((jobInvoiceItem) => {
                    if (
                        jobInvoiceItem.ID_Variation &&
                        jobInvoiceItem.ID_Variation === this.variation.ID_Variation &&
                        jobInvoiceItem.InvoiceTotalIncTax !== 0
                    ) {
                        this.isVariationInvoiced = true;
                        this.invoices.push(jobInvoice);
                    }
                });
            }
        });
    }

    // @ts-ignore TS7006
    taxRatesChanged(evt, item) {
        item.TaxRates = evt;
    }

    orderVariationItems() {
        // @ts-ignore TS2532
        for (let i = 0; i < this.variation.JobVariationTasks.length; i++) {
            // @ts-ignore TS2532
            this.variation.JobVariationTasks[i].Order = i;
        }
    }

    // @ts-ignore TS7006
    toggleShowInSchedule(task) {
        task.ShowInSchedule = !task.ShowInSchedule;

        if (task.ShowInSchedule) {
            this.scheduleChanges.added.add(task.ID_JobVariationTask);
            this.scheduleChanges.removed.delete(task.ID_JobVariationTask);
        } else {
            this.scheduleChanges.removed.add(task.ID_JobVariationTask);
            this.scheduleChanges.added.delete(task.ID_JobVariationTask);
        }
    }

    setParentTaskId(item: JobVariationTask, match: TypeaheadMatch) {
        const selectedItem = match.item;
        item.ID_ParentTask = selectedItem.ID_ParentTask;
        item.ParentTask = selectedItem.Description;
    }

    setParentTaskIdOnChange(item: JobVariationTask) {
        if (item.ParentTask === '') {
            // @ts-ignore TS2322
            item.ID_ParentTask = null; // Empty Parent Task, make sure ID_ParentTask is blank
        } else if (item.ParentTask !== '') {
            const parentTask = this.parentTasks.find((t) => {
                return t.Description === item.ParentTask;
            });
            if (parentTask) {
                item.ID_ParentTask = parentTask.ID_ParentTask; // Found matching category based on description
                // @ts-ignore TS2322
            } else item.ID_ParentTask = null; // No matching category, blank ID_Parent task so new one is created
        }
    }

    statusChanged() {
        if (!this.editing && this.variation.Status === 'Pending') this.editing = this.variation.Status === 'Pending';
    }

    showCostItemsModal(currentCostItem: JobVariationTask) {
        const search = currentCostItem.Description === 'New Item' ? '' : currentCostItem.Description;
        const costItemType = this.taxService.costItemTypeToDisplay(currentCostItem.CostItemType);
        this.priceListService
            .browse(search, true, currentCostItem.UOM, currentCostItem.UnitCost, costItemType)
            // @ts-ignore TS7006
            .then((selectedItems) => {
                if (selectedItems && selectedItems.length) {
                    this.setCostItem(currentCostItem, selectedItems[0]);
                }
            })
            .catch(() => ({}));
    }

    // @ts-ignore TS7006
    searchCostItems(value) {
        return this.cataloguesService.RecommendCostItems(value, 10, false, true);
    }

    setCostItem(variationItem: JobVariationTask, match: any) {
        const costItem = match.item || match;
        const id = costItem.ID_CostItem || costItem.costItemId;
        // set properties from selection
        variationItem.ID_CostItem = id;
        variationItem.Description = costItem.description;
        variationItem.UOM = costItem.uom;
        variationItem.UnitCost = costItem.unitCost;
        variationItem.CostItemType = costItem.costItemType;
        variationItem.ItemCode = costItem.itemCode;

        // Get latest version of item from server and update if necessary
        this.cataloguesService.GetCostItem(id).then((data) => {
            // @ts-ignore TS2531
            if (data.results.length > 0) {
                // @ts-ignore TS2531
                const item = data.results[0];
                variationItem.ID_CostItem = item.ID_CostItem;
                variationItem.Description = item.Description;
                variationItem.UOM = item.UOM;
                variationItem.UnitCost = item.UnitCost;
                variationItem.CostItemType = this.taxService.validateCostItemType(item.CostItemType);
                variationItem.ItemCode = item.ItemCode;
            }
        });
    }

    clearItemCode(item: JobVariationTask) {
        item.ItemCode = null;
    }

    showCalculator(item: JobVariationTask) {
        const config: BxCalculatorConfig = {
            expression: item.UnitsExpression ? item.UnitsExpression : item.Units.toString(),
            title: item.Description,
            item: item,
        };
        this.openCalculatorV2(config);
    }

    private openCalculatorV2(config: BxCalculatorConfig) {
        const estimate = this.variation.Job.Estimate;
        (estimate ? this.estimateMeasurementCategoryService.getEstimateMeasurementCategoriesByEstimateId(estimate.ID_Estimate) : of(null))
            // @ts-ignore TS2554
            .pipe(take(1))
            // @ts-ignore TS2554
            .subscribe((categories: EstimateMeasurementCategoryVM[]) => {
                if (categories) {
                    this.estimateMeasurementCalculatorService.updateConfigExpressionRefs(config, categories);
                }

                this.bxCalculatorService.open(config).then(
                    (result: any) => {
                        result.item.UnitsExpression = result.expression;
                        result.item.Units = result.value;
                    },
                    (cancel) => {
                        // do nothing
                    }
                );
            });
    }

    // @ts-ignore TS7006
    doTakeOff(item) {
        this.takeOffService
            .doTakeOff({
                referenceId: this.estimate.ID_Estimate,
                Id: item.ID_JobVariationTask,
                measurementId: item.ID_JobVariationTask,
                description: item.Description,
                uom: item.UOM,
            })
            .then(
                // @ts-ignore TS2345
                (result: TakeOffMeasurement) => {
                    item.Units = result.total;
                    // @ts-ignore TS2532
                    item.UnitsExpression = result.total.toString();
                    if (!item.UOM || item.UOM.toLowerCase() !== result.uom) item.UOM = result.uom;
                },
                (error) => {
                    //console.log('Modal dismissed')
                    if (error) this.logger.warning(error);
                }
            );
    }

    ok(send = false) {
        if (send && !this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        const msg = this.invalidMessage();
        if (msg) {
            this.logger.warning(msg);
            return;
        }

        this.variation.send = send;
        this.bsModalRef.hide();
        this.action.emit({ cancelled: false, variation: this.variation, scheduleChanges: this.scheduleChanges });
    }

    invalidMessage() {
        const emptyItems = _.filter(this.variation.JobVariationTasks, (item) => {
            return !item.IsDeleted && !item.Description;
        });
        if (emptyItems.length > 0) return `${this.translations.variation} items cannot be empty`;
        return undefined;
    }

    cancel() {
        this.bsModalRef.hide();
        this.action.emit({ cancelled: true, variation: this.variation });
    }

    addItem() {
        // @ts-ignore TS2532
        const order = this.variation.JobVariationTasks.length
            ? // @ts-ignore TS2532
              _.sortBy(this.variation.JobVariationTasks, 'Order').reverse()[0].Order + 1
            : 0;

        const data = {
            Units: 1,
            Order: order,
        };

        const newItem = this.repoJobs.CreateJobVariationTask(this.variation, data);
        this.orderVariationItems();
        this.variation.calculateTotal();
        focusElement(newItem.ID_JobVariationTask);
        return newItem;
    }

    // @ts-ignore TS7006
    deleteItem(item) {
        item.IsDeleted = true;
        if (item.ShowInSchedule) {
            this.scheduleChanges.added.delete(item.ID_JobVariationTask);
            this.scheduleChanges.removed.add(item.ID_JobVariationTask);
        }
        this.variation.calculateTotal();
    }

    itemCount() {
        // @ts-ignore TS2532
        return this.variation.JobVariationTasks.filter((t) => {
            return !t.IsDeleted;
        }).length;
    }

    validateCategories() {
        // @ts-ignore TS2532
        return this.variation.JobVariationTasks.some((t) => !t.IsDeleted && !t.ParentTask);
    }

    // @ts-ignore TS7006
    setupCostItems(search) {
        this.costItemSuggestions$ = new Observable<any>((observer: Subscriber<string>) => {
            // Runs on every search
            observer.next(search);
        }).pipe(
            debounceTime(500),
            distinctUntilChanged(),
            mergeMap(async (token: string) => {
                return await this.searchCostItems(token);
            })
        );
    }

    // @ts-ignore TS7006
    dropVariationItem($event) {
        // Only index of undeleted items matter for sorting
        // @ts-ignore TS2532
        const undeletedTasks = this.variation.JobVariationTasks.filter((task) => task.IsDeleted === false);
        const dragItem = undeletedTasks[$event.previousIndex];
        const dropItem = undeletedTasks[$event.currentIndex];
        // @ts-ignore TS2345
        this.changeItemOrder(dragItem, dropItem);
    }

    changeItemOrder(dragItem: JobVariationTask, dropItem: JobVariationTask) {
        if (!dragItem || !dropItem) return;
        if (dragItem === dropItem) return;
        // Get currently open parent
        // @ts-ignore TS2345
        moveInArray(this.variation.JobVariationTasks, dragItem, dropItem);
        this.orderVariationItems();
    }
}

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        BsDatepickerModule,
        SharedComponentsModule,
        NgSelectModule,
        NgxSelectModule,
        TaxRateSelectorModule,
        TooltipModule,
        TypeaheadModule,
        CurrencyInputModule,
        DragDropModule,
        SingleClickModule,
        CatalogueItemLinkTileModule,
        UiEstimateItemPillModule,
        AutoFocusDirective,
    ],
    declarations: [JobVariationDetailComponent],
    exports: [JobVariationDetailComponent],
})
export class JobVariationDetailModule {}
